import React from 'react';
import type { VisitWindow } from '../../../../../utils/observedAt';

/** The visit has ended, so an entry written now is documented after the end (late) */
export const visitHasEnded = (window: VisitWindow | undefined, nowMs: number = Date.now()): boolean => {
  const end = window?.endedAt ? Date.parse(window.endedAt) : NaN;
  return Number.isFinite(end) && end < nowMs;
};

export const LATE_NOTE = 'Documented after the visit ended: this entry will be labelled late.';

/**
 * Phase 16C "Observed at", reused by every Phase 18 form (M8). The default comes from
 * useChartingData: now during the visit, the visit end after it. After the visit ends the
 * field notes that the entry will be labelled late.
 */
const ObservedAtField: React.FC<{
  id?: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  disabled?: boolean;
  visitWindow?: VisitWindow;
  /** Phase 18U U12: phones and field-mode tablets keep 44 px / 16 px at any width */
  touch?: boolean;
}> = ({ id = 'entry-observed-at', label = 'Observed at', value, onChange, error, disabled = false, visitWindow, touch = false }) => (
  <div className="mb-2">
    <div className="flex flex-wrap items-center gap-2">
      <label htmlFor={id} className="text-xs font-medium text-gray-700">{label}</label>
      <input
        id={id}
        type="time"
        disabled={disabled}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`min-h-[44px] rounded border px-2 text-base ${touch ? '' : 'md:min-h-0 md:py-1 md:text-sm'} ${error ? 'border-red-400' : 'border-gray-300'} focus:border-blue-500 focus:outline-none`}
      />
      {error && <span id={`${id}-error`} role="alert" className="text-xs text-red-700">{error}</span>}
    </div>
    {visitHasEnded(visitWindow) && (
      <p className="mt-0.5 text-xs text-amber-700" data-testid="late-entry-note">{LATE_NOTE}</p>
    )}
  </div>
);

export default ObservedAtField;
